function cidrToRange(cidr) {
  const parts = cidr.split('/');
  if (parts.length !== 2) return null;
  const octets = parts[0].split('.').map(Number);
  const prefix = parseInt(parts[1], 10);
  if (octets.some((o) => isNaN(o) || o < 0 || o > 255)) return null;
  if (isNaN(prefix) || prefix < 0 || prefix > 32) return null;
  const ip = (octets[0] << 24) | (octets[1] << 16) | (octets[2] << 8) | octets[3];
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  const network = (ip & mask) >>> 0;
  const broadcast = (network | ~mask) >>> 0;
  return { network, broadcast };
}

function cidrContains(parentCidr, childCidr) {
  const parent = cidrToRange(parentCidr);
  const child = cidrToRange(childCidr);
  if (!parent || !child) return false;
  return child.network >= parent.network && child.broadcast <= parent.broadcast;
}

function cidrOverlaps(a, b) {
  const ra = cidrToRange(a);
  const rb = cidrToRange(b);
  if (!ra || !rb) return false;
  return ra.network <= rb.broadcast && rb.network <= ra.broadcast;
}

export function validateDesign(nodes, edges) {
  const errors = [];
  const warnings = [];

  const rgNodes = nodes.filter((n) => n.data?.type === 'resource-group');
  const vnetNodes = nodes.filter((n) => n.data?.type === 'vnet');
  const subnetNodes = nodes.filter((n) => n.data?.type === 'subnet');
  const vmNodes = nodes.filter((n) => n.data?.type === 'virtual-machine');
  const allNamed = nodes.filter((n) => n.data?.name);

  const seenNames = new Map();
  for (const node of allNamed) {
    const name = node.data.name;
    if (seenNames.has(name)) {
      errors.push(`Duplicate name "${name}" — resource names must be unique`);
    }
    seenNames.set(name, true);
  }

  const vnetsByRg = new Map();
  for (const vnet of vnetNodes) {
    const rgId = vnet.parentId;
    if (!vnetsByRg.has(rgId)) vnetsByRg.set(rgId, []);
    vnetsByRg.get(rgId).push(vnet);
  }

  for (const [, vnets] of vnetsByRg) {
    for (let i = 0; i < vnets.length; i++) {
      for (let j = i + 1; j < vnets.length; j++) {
        const a = vnets[i].data.cidr;
        const b = vnets[j].data.cidr;
        if (a && b && cidrOverlaps(a, b)) {
          errors.push(`VNet "${vnets[i].data.name}" (${a}) overlaps with VNet "${vnets[j].data.name}" (${b})`);
        }
      }
    }
  }

  for (const subnet of subnetNodes) {
    const parentVnet = vnetNodes.find((v) => v.id === subnet.parentId) ||
      vnetNodes.find((v) => v.id === edges.find((e) => e.target === subnet.id)?.source);

    if (parentVnet && subnet.data.cidr && parentVnet.data.cidr) {
      if (!cidrContains(parentVnet.data.cidr, subnet.data.cidr)) {
        errors.push(`Subnet "${subnet.data.name}" CIDR (${subnet.data.cidr}) is not within parent VNet "${parentVnet.data.name}" CIDR (${parentVnet.data.cidr})`);
      }
    }

    const siblings = subnetNodes.filter((s) =>
      s.id !== subnet.id && s.parentId === subnet.parentId
    );
    for (const sib of siblings) {
      if (subnet.data.cidr && sib.data.cidr && cidrOverlaps(subnet.data.cidr, sib.data.cidr)) {
        errors.push(`Subnet "${subnet.data.name}" (${subnet.data.cidr}) overlaps with subnet "${sib.data.name}" (${sib.data.cidr})`);
      }
    }
  }

  for (const vnet of vnetNodes) {
    const hasSubnet = subnetNodes.some((s) => s.parentId === vnet.id);
    const hasEdge = edges.some((e) => e.source === vnet.id && subnetNodes.some((s) => s.id === e.target));
    if (!hasSubnet && !hasEdge) {
      warnings.push(`VNet "${vnet.data.name}" has no subnets`);
    }
  }

  for (const vm of vmNodes) {
    const hasSubnet = vm.parentId && subnetNodes.some((s) => s.id === vm.parentId);
    const hasEdge = edges.some((e) => e.target === vm.id && subnetNodes.some((s) => s.id === e.source));
    if (!hasSubnet && !hasEdge && subnetNodes.length > 1) {
      warnings.push(`VM "${vm.data.name}" is not connected to a subnet — it will have no network interface`);
    }
  }

  const resourceTypes = ['vnet', 'subnet', 'virtual-machine', 'managed-disk', 'storage-account'];
  for (const node of nodes) {
    if (resourceTypes.includes(node.data?.type)) {
      const hasRg = node.parentId && rgNodes.some((r) => r.id === node.parentId);
      const hasRgName = node.data.resourceGroupName;
      const singleRg = rgNodes.length === 1;
      if (!hasRg && !hasRgName && !singleRg) {
        warnings.push(`"${node.data.name}" is not assigned to a resource group`);
      }
    }
  }

  return { errors, warnings };
}
